import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaUserMd, FaHospital, FaFileInvoiceDollar, FaLaptopMedical, FaHandHoldingHeart, FaStethoscope,
    FaArrowRight, FaCheckCircle
} from 'react-icons/fa';
import heroImage from '../../assets/Healthcare Professional with Stethoscope in Hospital Setting - Medical Technology Theme.png';

const PayerProviderPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaLaptopMedical, title: 'Telehealth Platforms', desc: 'Secure, high-quality video consultation apps for remote care.', image: 'https://images.unsplash.com/photo-1576091160550-21876ce7234f?w=800&q=80' },
        { icon: FaUserMd, title: 'Provider Portals', desc: 'Unified dashboards for clinicians to access patient data.', image: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=800&q=80' },
        { icon: FaFileInvoiceDollar, title: 'Claims Processing', desc: 'Automated claims adjudication using AI and NLP.', image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80' },
        { icon: FaHospital, title: 'Hospital Management', desc: 'Integrated HIS for operational efficiency and patient flow.', image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80' },
        { icon: FaHandHoldingHeart, title: 'Patient Experience', desc: 'Mobile apps for appointment booking and engagement.', image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=800&q=80' },
        { icon: FaStethoscope, title: 'Interoperability', desc: 'FHIR strategies for seamless data exchange between systems.', image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80' }
    ];

    const faqs = [
        { q: 'Do your platforms support FHIR standards?', a: 'Yes, we build all our interoperability solutions on the HL7 FHIR standard for seamless data exchange.' },
        { q: 'How do you ensure HIPAA compliance?', a: 'We implement strict access controls, encryption, and audit logs to ensure full HIPAA compliance.' },
        { q: 'Can you integrate with Epic/Cerner?', a: 'We have extensive experience integrating custom applications with major EHRs like Epic, Cerner, and Allscripts.' },
        { q: 'Do you offer patient engagement apps?', a: 'Yes, we build white-label mobile apps for scheduling, result viewing, and patient-provider communication.' },
        { q: 'How can AI help with claims?', a: 'Our AI models can automatically validate claims against policy rules, reducing denials and manual rework.' },
        { q: 'What about telehealth security?', a: 'We use end-to-end encrypted WebRTC channels to ensure private and secure video consultations.' }
    ];

    return (
        <div className="bg-white font-sans text-slate-900">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-top"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 via-emerald-900/60 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-emerald-300 mb-4">Industries &gt; Healthcare</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Patient <span className="font-bold text-emerald-400">First</span></h1>
                        <p className="text-lg font-light text-emerald-50 leading-relaxed mb-8">
                            Seamless digital experiences for providers and payers.
                        </p>
                        <div className="flex gap-4">
                            <a href="#contact" className="px-8 py-3 bg-emerald-600 text-white font-medium rounded-sm hover:bg-emerald-700 transition-all text-sm uppercase tracking-wide">
                                Healthcare Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-light mb-6 text-slate-900">Care Without Boundaries</h2>
                            <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                The healthcare ecosystem is shifting towards value-based care. Tech-Inovant empowers providers and payers to deliver better outcomes at lower costs through digital innovation.
                            </p>
                            <p className="text-slate-600 leading-relaxed mb-8">
                                We bridge the gap between clinical workflows and administrative efficiency, putting the patient back at the center of care.
                            </p>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-emerald-600" />
                                    <span className="text-slate-700 font-medium">Interoperable Systems</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-emerald-600" />
                                    <span className="text-slate-700 font-medium">Telehealth Integration</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-emerald-600" />
                                    <span className="text-slate-700 font-medium">Value-Based Analytics</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80"
                                alt="Hospital Care"
                                className="rounded-sm shadow-sm"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-emerald-600 text-white p-6 rounded-sm shadow-lg hidden md:block">
                                <div className="text-3xl font-bold">24/7</div>
                                <div className="text-xs uppercase tracking-wider">Patient Support</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="mb-16">
                        <h2 className="text-3xl font-light mb-4 text-slate-900">Provider & Payer Services</h2>
                        <div className="w-20 h-1 bg-emerald-600"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-white border border-slate-200 hover:border-emerald-400 transition-colors p-0 overflow-hidden group">
                                <div className="h-48 overflow-hidden relative">
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-8">
                                    <service.icon className="text-3xl text-emerald-600 mb-4" />
                                    <h3 className="text-xl font-medium mb-3 text-slate-900">{service.title}</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                                        {service.desc}
                                    </p>
                                    <a href="#" className="inline-flex items-center text-sm font-bold text-emerald-700 hover:text-emerald-900 tracking-wide uppercase">
                                        Details <FaArrowRight className="ml-2 text-xs" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Metrics */}
            <section className="py-16 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <h2 className="text-3xl font-light mb-8 text-center text-slate-900">System-Wide Results</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: 'Regional Hospital', stat: '-30%', desc: 'Reduction in readmission rates via remote monitoring.' },
                            { title: 'Health Plan', stat: '85%', desc: 'Auto-adjudication rate for claims.' },
                            { title: 'Telehealth', stat: '1M+', desc: 'Consultations powered by our platform.' }
                        ].map((caseStudy, idx) => (
                            <div key={idx} className="bg-slate-50 p-8 border-l-4 border-emerald-500">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{caseStudy.title}</div>
                                <div className="text-4xl font-light text-emerald-800 mb-4">{caseStudy.stat}</div>
                                <p className="text-slate-600">{caseStudy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Section */}
            <section id="contact" className="py-12 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <h2 className="text-3xl font-light mb-8 text-slate-900">Healthcare FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="bg-white border border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-50"
                                        >
                                            <span className="font-medium text-slate-800">{faq.q}</span>
                                            <span className="text-slate-400">{openFaq === idx ? '-' : '+'}</span>
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                                                {faq.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-white p-8 rounded-sm shadow-sm border border-slate-200">
                            <h3 className="text-2xl font-light mb-6 text-slate-900">Improve Patient Outcomes</h3>
                            <p className="text-slate-600 mb-8">Discuss your digital health strategy.</p>
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="Sarah Green" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Organization</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="City General Hospital" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="sarah@citygeneral.org" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Focus</label>
                                    <select className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-emerald-500 outline-none transition-colors text-slate-700">
                                        <option>Telehealth</option>
                                        <option>EHR Integration</option>
                                        <option>Patient Engagement</option>
                                        <option>Claims Management</option>
                                    </select>
                                </div>
                                <button type="submit" className="w-full bg-emerald-600 text-white font-bold py-4 rounded-sm hover:bg-emerald-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Contact Us
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="py-12 bg-slate-900 text-white text-center">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-light mb-6">Digitize Your Care</h2>
                    <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                        Delivering better care through smarter technology.
                    </p>
                    <button className="px-8 py-3 bg-white text-slate-900 font-bold uppercase tracking-wide hover:bg-emerald-50 transition-colors">
                        Get Started
                    </button>
                </div>
            </section>
        </div>
    );
};

export default PayerProviderPage;
